import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { cn } from '@mlm/ui/lib/cn';

/**
 * A full-screen tool (a breathing session, a listening session, the weekly
 * review): brand and Close, no tab bar. The blueprint hides the bottom
 * navigation during rituals and playback so one thing has the member's
 * attention.
 */
export function ToolFrame({ close, children }: { close: string; children: ReactNode }) {
  return (
    <div className="mx-auto flex min-h-screen w-full max-w-xl flex-col px-5 pb-12 pt-6 lg:max-w-[640px] lg:pt-16">
      <div className="mb-8 flex items-center justify-between lg:mb-12">
        <span className="font-display text-lg font-semibold text-ink">Miracles Protocol</span>
        <Link to={close} className="eyebrow hover:text-ink">
          Close
        </Link>
      </div>
      {children}
    </div>
  );
}

/**
 * An immersive session (2026-10 redesign): breathing or listening on a
 * forest-green ground, a round Close and the session's name, nothing else.
 * Error and lock states stay on the light `ToolFrame`.
 */
export function ImmersiveFrame({
  close,
  eyebrow,
  ground,
  children,
}: {
  close: string;
  eyebrow: ReactNode;
  ground: 'session' | 'listen';
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex min-h-[100dvh] flex-col text-paper',
        ground === 'session' ? 'bg-session' : 'bg-listen',
      )}
    >
      <div className="mx-auto flex w-full max-w-[520px] flex-1 flex-col px-[22px] pb-10 pt-6 lg:pt-14">
        <div className="flex items-center justify-between gap-3">
          <Link
            to={close}
            aria-label="Close"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-paper/[0.16] bg-paper/[0.06] transition-colors hover:border-paper/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E6C87A]"
          >
            <X className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
          </Link>
          <span className="min-w-0 truncate text-center font-mono text-[0.66rem] font-medium uppercase tracking-[0.16em] text-paper/60">
            {eyebrow}
          </span>
          <span className="w-10 shrink-0" aria-hidden />
        </div>
        {children}
      </div>
    </div>
  );
}

/** A pill on a dark ground: gold is the one decision, glass everything else. */
export const darkPillGold =
  'flex min-h-14 items-center justify-center gap-2.5 rounded-full bg-gold px-[18px] font-mono text-[0.72rem] font-medium uppercase tracking-[0.16em] text-ink transition-colors hover:bg-[#c29d4c] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E6C87A] disabled:opacity-50';
export const darkPillGlass =
  'flex min-h-14 items-center justify-center gap-2.5 rounded-full border border-paper/[0.22] bg-paper/[0.06] px-[18px] font-mono text-[0.72rem] font-medium uppercase tracking-[0.16em] text-paper transition-colors hover:border-paper/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E6C87A] disabled:opacity-50';

export const toolHeading =
  'font-display text-[1.9rem] font-semibold leading-[1.12] text-ink lg:text-[2.5rem] lg:leading-[1.1]';
